'use client';

import React, { useState, useEffect } from 'react';
import { clsx } from 'clsx';
import { useEffectOnce } from 'app/libs/useEffectOnce';
import { SWRConfig } from 'swr';
import {
    LAYOUT_SECTION_NAME,
    CSSVariablesMap,
    FEATURE_FLAG_KEYS,
} from 'shop-ui/constants';
import { PageLayoutDetail, type PageLayoutData, type BuyerSSRProps } from 'shop-ui/types';
import { buyerRenderUI } from 'app/shop-ui/tools/buyerRenderUI';
import { Header, Footer } from 'app/shop-ui/section';
import { useBuyerBrandStore } from 'app/store/brand';
import { ClientBody } from 'buyer/ClientBody';
import { useBuyerSideStyle } from './useBuyerSideStyle';
import { SiteConfigProvider, type SiteConfigProviderProps } from './SiteConfigProvider';
import './GeneralEnhancePage.scss';

export interface ClientPageProps<T> {
    headerLayout: PageLayoutDetail | null;
    footerLayout: PageLayoutDetail | null;
    bodyLayout?: PageLayoutDetail;
    isNotFound?: boolean;
    isMobile?: boolean;
    children?: React.ReactNode;
    config: SiteConfigProviderProps['config'];
    __ssr?: BuyerSSRProps<T>;
}

const BuyerCustomSection = React.memo(
    (props: {
        layoutId: string;
        layout: PageLayoutData;
        sectionName: LAYOUT_SECTION_NAME;
        __ssr: BuyerSSRProps;
    }) => {
        const { layoutId, layout, sectionName, __ssr } = props;

        return buyerRenderUI(layoutId, layout, sectionName, __ssr);
    }
);

const getSSRBodyWidth = (value: string | undefined) => {
    switch (value) {
        case 'custom':
            return value;

        case 'large':
            return '1200px';

        case 'medium':
            return '1000px';

        case 'small':
            return '768px';

        default:
            return 'auto';
    }
};

BuyerCustomSection.displayName = 'BuyerCustomSection';

export const GeneralEnhancePage: React.FC<ClientPageProps<unknown>> = (props) => {
    const {
        headerLayout,
        footerLayout,
        bodyLayout,
        config,
        children,
        isNotFound,
        __ssr = {},
    } = props;

    const [hydrated, setHydrated] = useState(false);

    const { setTaxInfo, setBrandMeta } = useBuyerBrandStore();

    const { siteStyle, topLevelFontStyle, headerHeight, footerHeight } =
        useBuyerSideStyle({
            siteStyle: bodyLayout?.layout.site || {},
            headerLayoutStyle: headerLayout?.layout.style || {},
            footerLayoutStyle: footerLayout?.layout.style || {},
            bodyLayoutStyle: bodyLayout?.layout.style || {},
        });

    useEffectOnce(() => {
        setTaxInfo(config.taxInfo);

        if (config.brandMeta) {
            setBrandMeta(config.brandMeta);
        }
    });

    useEffect(() => {
        setHydrated(true);
    }, []);

    if (!hydrated) {
        const ssrBodyWidth = bodyLayout?.layout.site?.[FEATURE_FLAG_KEYS.SITE_WIDTH];

        return (
            <body
                className={clsx('layout-wrapper', {
                    'is-not-found': isNotFound,
                })}
            >
                {headerLayout &&
                    (siteStyle.useHeader ||
                        (headerLayout && !bodyLayout) ||
                        isNotFound) && (
                        <Header
                            __ssr={{
                                layout: headerLayout.layout,
                            }}
                            isBuyer
                        >
                            <BuyerCustomSection
                                layoutId={headerLayout.layoutId}
                                layout={headerLayout.layout}
                                sectionName={LAYOUT_SECTION_NAME.HEADER}
                                __ssr={__ssr}
                            />
                        </Header>
                    )}
                <main className="shop-custom-page-main">
                    {bodyLayout && (
                        <div
                            style={{
                                margin: '0 auto',
                                '--shop-ui-custom-site-width':
                                    getSSRBodyWidth(ssrBodyWidth),
                            }}
                        >
                            <BuyerCustomSection
                                layoutId={bodyLayout.layoutId}
                                layout={bodyLayout.layout}
                                sectionName={LAYOUT_SECTION_NAME.BODY}
                                __ssr={__ssr}
                            />
                        </div>
                    )}
                    {children}
                </main>
                {footerLayout && (
                    <Footer isBuyer>
                        <BuyerCustomSection
                            layoutId={footerLayout.layoutId}
                            layout={footerLayout.layout}
                            sectionName={LAYOUT_SECTION_NAME.FOOTER}
                            __ssr={__ssr}
                        />
                    </Footer>
                )}
            </body>
        );
    }

    return (
        <ClientBody>
            <SWRConfig
                value={{
                    shouldRetryOnError: false,
                    errorRetryCount: 0,
                }}
            >
                <SiteConfigProvider config={config} />
                <div
                    className={clsx('layout-wrapper', {
                        'is-not-found': isNotFound,
                    })}
                    style={{
                        '--shop-ui-custom-site-header-height': headerHeight,
                        '--shop-ui-custom-site-footer-height': footerHeight,
                        ...topLevelFontStyle,
                    }}
                >
                    {headerLayout &&
                        (siteStyle.useHeader ||
                            (headerLayout && !bodyLayout) ||
                            isNotFound) && (
                            <Header
                                __ssr={{
                                    layout: headerLayout.layout,
                                }}
                                isBuyer
                            >
                                <BuyerCustomSection
                                    layoutId={headerLayout.layoutId}
                                    layout={headerLayout.layout}
                                    sectionName={LAYOUT_SECTION_NAME.HEADER}
                                    __ssr={__ssr}
                                />
                            </Header>
                        )}
                    <main
                        className="shop-custom-page-main"
                        style={{
                            ...(siteStyle[
                                CSSVariablesMap[FEATURE_FLAG_KEYS.SITE_WIDTH]
                            ] && {
                                '--shop-ui-custom-site-width':
                                    siteStyle[
                                        CSSVariablesMap[FEATURE_FLAG_KEYS.SITE_WIDTH]
                                    ],
                            }),
                            ...(siteStyle[
                                CSSVariablesMap[FEATURE_FLAG_KEYS.SITE_BACKGROUND_COLOR]
                            ] && {
                                background:
                                    siteStyle[
                                        CSSVariablesMap[
                                            FEATURE_FLAG_KEYS.SITE_BACKGROUND_COLOR
                                        ]
                                    ],
                            }),
                        }}
                    >
                        {bodyLayout && (
                            <BuyerCustomSection
                                layoutId={bodyLayout.layoutId}
                                layout={bodyLayout.layout}
                                sectionName={LAYOUT_SECTION_NAME.BODY}
                                __ssr={__ssr}
                            />
                        )}
                        {children}
                    </main>
                    {footerLayout && (
                        <Footer isBuyer>
                            <BuyerCustomSection
                                layoutId={footerLayout.layoutId}
                                layout={footerLayout.layout}
                                sectionName={LAYOUT_SECTION_NAME.FOOTER}
                                __ssr={__ssr}
                            />
                        </Footer>
                    )}
                </div>
            </SWRConfig>
        </ClientBody>
    );
};
